import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { css } from '@kufox/mui/emotion';
import Dropdown from '@kufox/mui/Dropdown';
import useTheme from '@kufox/mui/hooks/useTheme';
import useSnackbar from '@kufox/mui/hooks/useSnackbar';
import { CopyOutlined, ArrowOutlined } from '@kufox/icons';
import { throttle } from 'lodash';
import CopyToClipboard from 'react-copy-to-clipboard';
import Link from '../../components/Link';
import AssetsBox from '../AssetsBox';
import OrderBox from '../OrderBox';
import { useLang } from '../../hookTool';
import { addSpmIntoQuery, resolveFee, kcsensorsClick } from '../../common/tools';
import { namespace } from '../model';
import { long_language } from '../config';

const useStyles = ({ color, inDrawer, isLong_language }) => {
  return {
    root: css`
      display: flex;
      align-items: center;
    `,
    navItem: css`
      color: ${color.text};
      cursor: pointer;
      font-size: 14px;
      line-height: 22px;
      &:hover {
        color: ${color.primary};
      }
    `,
    userFlag: css`
      border: 1px solid ${color.text};
      border-radius: 50%;
      width: 40px;
      height: 40px;
      line-height: 40px;
      text-align: center;
      cursor: pointer;
      color: ${color.text};
      font-size: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      &:hover {
        opacity: 0.6;
      }
      &:active {
        opacity: 1;
      }
    `,
    overlayWrapper: css`
      width: ${inDrawer ? 'auto' : isLong_language ? '400px' : '300px'};
      margin-top: ${inDrawer ? '0' : '20px'};
      padding: ${inDrawer ? '0' : '16px 0'};
      background: ${color.base};
      box-shadow: ${inDrawer ? 'none' : '0px 10px 60px rgba(0, 0, 0, 0.1)'};
      border-radius: 4px;
      & hr {
        margin: 8px 24px;
      }
    `,
    menuItem: css`
      display: block;
      padding: 14px 24px;
      cursor: pointer;
      color: ${color.text};
      font-family: ${inDrawer ? 'URWDIN-Medium ' : ''};
      text-decoration: none !important;
      font-size: ${inDrawer ? '14px' : '16px'};
      line-height: 1.4;
      position: relative;
      white-space: break-spaces;
      word-break: break-all;
      &:hover {
        background: ${color.cover4};
        color: ${color.text};
        & .uid {
          border-color: ${color.divider};
        }

        & .in {
          border-color: ${color.divider};
        }
      }
      @keyframes move {
        0% {
          right: 20px;
        }
        100% {
          right: 18px;
        }
      }
      @keyframes changeColor {
        0% {
          fill: #737e8d;
        }
        100% {
          fill: #18bb97;
        }
      }
      & .arrow {
        display: none;
      }
      &:hover .arrow {
        display: block;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 20px;
        animation: move 0.3s linear forwards;
        -webkit-animation: move 0.3s linear forwards;
        & path {
          animation: changeColor 0.3s linear forwards;
          -webkit-animation: changeColor 0.3s linear forwards;
        }
      }
      &.center {
        text-align: center;
      }
      & .nameView {
        display: flex;
        align-items: center;
      }
      & .email {
        font-size: 20px;
        line-height: 1.4;
        color: ${color.text};
        margin-right: 8px;
      }
      & .verify {
        padding: 0px 8px;
        width: auto;
        height: 20px;
        line-height: 20px;
        text-align: center;
        border-radius: 2px;
        background: ${color.secondary};
        font-size: 12px;
        color: ${color.text40};
        border: 1px solid transparent;
      }
      & .in {
      }
      & .no {
        background: rgba(255, 181, 71, 0.12);
        color: ${color.complementary};
      }
      & .already {
        background: rgba(36, 174, 143, 0.12);
        color: ${color.primary};
      }
      & .tagView {
        display: flex;
        align-items: center;
        margin-top: 8px;
      }
      & .subAccount {
        font-size: 12px;
        line-height: 1.5;
        color: ${color.text60};
        margin-right: 8px;
      }
      & .uid {
        display: flex;
        align-items: center;
        width: auto;
        height: auto;
        padding: 2px 8px;
        background: ${color.cover4};
        border-radius: 12px;
        font-size: 12px;
        line-height: 1.6;
        color: ${color.text60};
        cursor: pointer;
        border: 1px solid transparent;
      }
      & .tipsIcon {
        display: inline-block;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: ${color.complementary};
        margin-right: 8px;
        margin-left: -16px;
      }
      &.alignCenter {
        display: flex;
        align-items: center;
      }
    `,
    vip: css`
      padding: 13px 24px;
      font-size: 14px;
      color: ${color.text60};
      margin-top: 8px;
      line-height: 28px;
      position: relative;
      &:hover {
        background: ${color.cover4};
        color: ${color.text};
      }
    `,
    vipInner: css`
      display: block;
      color: ${color.text};
      font-family: ${inDrawer ? 'URWDIN-Medium ' : ''};
      text-decoration: none !important;
      &:hover {
        color: ${color.text};
      }
      @keyframes move {
        0% {
          right: 20px;
        }
        100% {
          right: 18px;
        }
      }
      @keyframes changeColor {
        0% {
          fill: #737e8d;
        }
        100% {
          fill: #18bb97;
        }
      }
      & .arrow {
        display: none;
      }
      &:hover .arrow {
        display: block;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 20px;
        animation: move 0.3s linear forwards;
        -webkit-animation: move 0.3s linear forwards;
        & path {
          animation: changeColor 0.3s linear forwards;
          -webkit-animation: changeColor 0.3s linear forwards;
        }
      }
    `,
    maker: css`
      margin-right: 10px;
      line-height: 16px;
    `,
    number: css`
      font-size: 14px;
      color: ${color.text};
    `,
    kcs_discount_status: css`
      color: ${color.primary};
      font-size: 12px;
    `,
    kcs_discount: css`
      color: ${color.text};
      font-size: 12px;
      margin-top: 10px;
    `,
    hr: css`
      background: ${color.divider};
      border: none;
      height: 1px;
    `,
    links: css`
      display: flex;
      flex-direction: column;
    `,
    link: css`
      font-size: 14px;
      line-height: 22px;
      color: #00142a;
      text-decoration: none;
      font-family: URWDIN-Medium;
      &:hover {
        color: ${color.primary};
      }
      &.mg {
        margin-bottom: 16px;
      }
    `,
  };
};

const doNothing = (e) => {
  e.stopPropagation();
  e.preventDefault();
};
const kycStatusNo = 'UNVERIFIED';
const kycStatusAlready = 'VERIFIED';
// const kycStatusIng = 'VERIFING';

// 获取用户表示标示：昵称>邮箱>手机，从用户已有的里面选优先级最高的那个，取前两个字符展示
const getUserFlag = (user, isSub = false) => {
  const { nickname = '', email = '', phone = '', subAccount = '' } = user || {};
  let userFlag = '';
  try {
    if (nickname) {
      const nicknameStr = `${nickname}`;
      userFlag += nicknameStr[0];
      if (
        nicknameStr[1] &&
        nicknameStr[0].charCodeAt() <= 255 &&
        nicknameStr[1].charCodeAt() <= 255
      ) {
        userFlag += nicknameStr[1];
      }
    } else if (email) {
      userFlag += email.substring(0, 2);
    } else if (phone) {
      userFlag += phone.substring(phone.length - 2);
    }
    if (isSub) {
      userFlag = subAccount.substring(0, 2) || '';
    }
  } catch (e) {
    console.log(e);
  }
  return userFlag.toUpperCase();
};

export default (props) => {
  const {
    currentLang,
    userInfo,
    hostConfig,
    isSub = false,
    handleShowDrawer,
    minNav,
    inDrawer,
  } = props;
  const theme = useTheme();
  const isLong_language = long_language.indexOf(currentLang) > -1;
  const classes = useStyles({ color: theme.colors, inDrawer, isLong_language });
  const { t } = useLang();

  const { levelInfo, feeDiscountConfig, userKcsDiscountStatus, verifyStatus } = useSelector(
    (state) => state[namespace],
  );
  const { honorLevel = -1 } = levelInfo || {};
  const dispatch = useDispatch();

  const { enqueueSnackbar } = useSnackbar();

  const handleLogout = (e) => {
    kcsensorsClick(['person', !(currentLang.indexOf('zh_') === 0) && !isSub ? '9' : '8']);
    if (e && e.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
    }
    const to = '';
    dispatch({ type: `${namespace}/logout`, payload: { to, spm: ['person', '9'] } });
  };

  const handleMouseEnter = throttle(
    () => {
      dispatch({
        type: `${namespace}/pullUserLevel`,
      });
      if (!isSub) {
        dispatch({
          type: `${namespace}/pullKycStatus`,
        });
      }
    },
    2000,
    { leading: true },
  );

  const handleRouter = useCallback(() => {
    kcsensorsClick(['person', '1']);
    const { KUCOIN_HOST } = hostConfig;
    window.location.href = addSpmIntoQuery(
      `${KUCOIN_HOST}/account?lang=${currentLang}`,
      ['person', '1'],
      currentLang,
    );
  }, [currentLang]);

  const Overlay = () => {
    const { KUCOIN_HOST } = hostConfig;
    const { nickname = '', email = '', phone = '', subAccount = '' } = userInfo || {};
    const userName = isSub ? subAccount : nickname || email || phone || '';
    const accountHref = isSub ? `${KUCOIN_HOST}/account/security` : `${KUCOIN_HOST}/account`;
    return (
      <div css={classes.overlayWrapper}>
        <Link
          href={addSpmIntoQuery(accountHref, ['person', '2'], currentLang)}
          css={classes.menuItem}
          data-ga="person"
          data-modid="person"
          data-idx={2}
          lang={currentLang}
        >
          <div className="nameView">
            <span className="email">{userName}</span>
            {verifyStatus ? (
              <span
                className={`verify ${
                  verifyStatus === kycStatusNo
                    ? 'no'
                    : verifyStatus === kycStatusAlready
                    ? 'already'
                    : 'in'
                }`}
              >
                {verifyStatus === kycStatusNo
                  ? t('newheader.authenticator.no')
                  : verifyStatus === kycStatusAlready
                  ? t('newheader.authenticator.already')
                  : t('newheader.authenticator.in')}
              </span>
            ) : null}
          </div>
          <div className="tagView">
            {isSub && <span className="subAccount">{t('subaccount.subaccount')} : </span>}
            <div onClick={doNothing}>
              <CopyToClipboard
                text={userInfo.uid}
                onCopy={() => {
                  enqueueSnackbar.success(t('copy.succeed'));
                }}
              >
                <div className="uid">
                  UID: {userInfo.uid}
                  <CopyOutlined size="18" style={{ marginLeft: 4 }} />
                </div>
              </CopyToClipboard>
            </div>
          </div>
          <ArrowOutlined width="13px" height="13px" className="arrow" />
        </Link>
        <hr css={classes.hr} />
        <div css={classes.vip}>
          {honorLevel === -1 ? null : (
            <Link
              css={classes.vipInner}
              href={addSpmIntoQuery(`${KUCOIN_HOST}/vip/level`, ['person', '3'], currentLang)}
              data-modid="person"
              data-idx={3}
              lang={currentLang}
            >
              <div>
                <span css={classes.maker}>
                  {t('n.vip.fee.maker')}:{' '}
                  <span css={classes.number}>
                    {resolveFee(
                      levelInfo.makerFeeRate,
                      userKcsDiscountStatus,
                      feeDiscountConfig.discountRate,
                    )}
                  </span>
                </span>
                {t('n.vip.fee.taker')}:{' '}
                <span css={classes.number}>
                  {resolveFee(
                    levelInfo.takerFeeRate,
                    userKcsDiscountStatus,
                    feeDiscountConfig.discountRate,
                  )}
                </span>
              </div>
              {userKcsDiscountStatus && !isSub ? (
                <div css={classes.kcs_discount}>
                  {t('nav.fee.kcs_tip')}:
                  <span css={classes.kcs_discount_status}>{t('nav.fee.kcs_tip.status')}</span>
                </div>
              ) : null}
              <ArrowOutlined width="13px" height="13px" className="arrow" />
            </Link>
          )}
        </div>
        <hr css={classes.hr} />
        {inDrawer && (
          <>
            <div css={classes.links}>
              <OrderBox {...props} themeColors={theme.colors} title={t('nav.order')} />
              <AssetsBox {...props} themeColors={theme.colors} title={t('header.menu.assets')} />
            </div>
            <hr css={classes.hr} />
          </>
        )}
        {!isSub && (
          <Link
            href={addSpmIntoQuery(`${KUCOIN_HOST}/vip/level`, ['person', '4'], currentLang)}
            css={classes.menuItem}
            data-modid="person"
            data-idx={4}
            lang={currentLang}
          >
            {t('n.vip.fee.url')}
            <ArrowOutlined width="13px" height="13px" className="arrow" />
          </Link>
        )}
        <Link
          href={addSpmIntoQuery(
            `${KUCOIN_HOST}/account/security`,
            ['person', !isSub ? '5' : '4'],
            currentLang,
          )}
          css={classes.menuItem}
          data-modid="person"
          data-idx={!isSub ? '5' : '4'}
          lang={currentLang}
        >
          {t('account.security')}
          <ArrowOutlined width="13px" height="13px" className="arrow" />
        </Link>
        {!isSub && (
          <Link
            href={addSpmIntoQuery(`${KUCOIN_HOST}/account/kyc`, ['person', '6'], currentLang)}
            className="alignCenter"
            css={classes.menuItem}
            data-modid="person"
            data-idx={6}
            lang={currentLang}
          >
            {verifyStatus === kycStatusNo ? <span className="tipsIcon" /> : null}
            {t('kyc.verify')}
            <ArrowOutlined width="13px" height="13px" className="arrow" />
          </Link>
        )}
        {!(currentLang.indexOf('zh_') === 0) && !isSub && (
          <Link
            href={addSpmIntoQuery(
              `${KUCOIN_HOST}/assets/bonus/referral`,
              ['person', '7'],
              currentLang,
            )}
            css={classes.menuItem}
            data-modid="person"
            data-idx={7}
            lang={currentLang}
          >
            {t('nav.drop.invitation')}
            <ArrowOutlined width="13px" height="13px" className="arrow" />
          </Link>
        )}
        {!isSub && (
          <Link
            href={addSpmIntoQuery(
              `${KUCOIN_HOST}/account/api`,
              ['person', !(currentLang.indexOf('zh_') === 0) && !isSub ? '8' : '7'],
              currentLang,
            )}
            css={classes.menuItem}
            data-modid="person"
            data-idx={!(currentLang.indexOf('zh_') === 0) && !isSub ? '8' : '6'}
            lang={currentLang}
          >
            {t('drop.api.manage')}
            <ArrowOutlined width="13px" height="13px" className="arrow" />
          </Link>
        )}
        {!isSub && (
          <>
            <hr css={classes.hr} />
            <Link
              href={addSpmIntoQuery(
                `${KUCOIN_HOST}/account/sub`,
                ['person', !(currentLang.indexOf('zh_') === 0) && !isSub ? '9' : '8'],
                currentLang,
              )}
              css={classes.menuItem}
              data-modid="person"
              data-idx={!(currentLang.indexOf('zh_') === 0) && !isSub ? '9' : '8'}
              lang={currentLang}
            >
              {t('subaccount.subaccount')}
              <ArrowOutlined width="13px" height="13px" className="arrow" />
            </Link>
          </>
        )}
        <hr css={classes.hr} />
        <div
          onClick={handleLogout}
          className="center"
          css={classes.menuItem}
          data-modid="person"
          data-idx={!(currentLang.indexOf('zh_') === 0) && !isSub ? '10' : '9'}
        >
          {t('logout')}
        </div>
      </div>
    );
  };
  if (inDrawer) {
    return <Overlay />;
  }
  return (
    <div css={classes.root}>
      {minNav ? (
        <div
          onClick={() => {
            handleShowDrawer('user', true);
          }}
          css={classes.userFlag}
          onMouseEnter={handleMouseEnter}
          data-lang={currentLang}
        >
          {getUserFlag(userInfo, isSub)}
        </div>
      ) : (
        <Dropdown
          trigger="hover"
          overlay={<Overlay />}
          css={classes.navItem}
          placement="bottom"
          anchorProps={{ style: { display: 'block' } }}
        >
          <div onClick={handleRouter} css={classes.userFlag} onMouseEnter={handleMouseEnter}>
            {getUserFlag(userInfo, isSub)}
          </div>
        </Dropdown>
      )}
    </div>
  );
};
